/**
 * AdminAuditLog — owner-only audit log viewer.
 *
 * Live-refresh every 30 s (poll — cheaper than SSE for this frequency
 * and matches the pattern already used in AdminCostDashboard). Filter
 * by action + actor_id + time window. Data is opaque to the frontend
 * — we just render what the backend returned so any new event type
 * lights up automatically without a code change.
 *
 * Backend refuses reads to non-owner admins with 403 — we surface
 * that as a friendly "Owner only" banner instead of an error.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Loader2, ShieldAlert, Filter, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { adminAuditLog, adminAuditLogActions } from "@/lib/vendorApi";

const REFRESH_MS = 30_000;
const PAGE_SIZE = 50;

// Short human labels for the actor_type dot colours.
const ACTOR_STYLE = {
  admin: "bg-[#D4AF37]",
  system: "bg-white/50",
  vendor: "bg-emerald-400",
  user: "bg-sky-400",
};

// Colour a row based on the action's severity/family.
function actionTint(action) {
  if (!action) return "text-white/70";
  if (action.startsWith("admin.login.failed")) return "text-rose-300";
  if (action.startsWith("admin.")) return "text-[#F3CE5A]";
  if (action.startsWith("payout.")) return "text-emerald-300";
  if (action.startsWith("vendor.status_change")) return "text-sky-300";
  if (action.startsWith("commission.")) return "text-amber-300";
  return "text-white/75";
}

export default function AdminAuditLog({ onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ownerOnly, setOwnerOnly] = useState(false);
  const [filterAction, setFilterAction] = useState("");
  const [filterSinceHours, setFilterSinceHours] = useState("");
  const [availableActions, setAvailableActions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await adminAuditLog({
        action: filterAction || undefined,
        sinceHours: filterSinceHours ? Number(filterSinceHours) : undefined,
        limit: PAGE_SIZE,
        skip,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        onUnauthorized?.();
        return;
      }
      if (status === 403) {
        setOwnerOnly(true);
        return;
      }
      setError(err?.response?.data?.detail || "Could not load audit log.");
      toast.error(err?.response?.data?.detail || "Could not load audit log.");
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterSinceHours, skip, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  // Reset pagination when the filter changes.
  useEffect(() => { setSkip(0); }, [filterAction, filterSinceHours]);

  // Live refresh every 30 s — only when the tab is visible (skip the
  // network work if the user tabbed away).
  useEffect(() => {
    if (ownerOnly) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load, ownerOnly]);

  // Fetch distinct actions once for the filter dropdown.
  useEffect(() => {
    if (ownerOnly) return;
    (async () => {
      try {
        const d = await adminAuditLogActions();
        setAvailableActions(d.actions || []);
      } catch (_e) {
        // silent — fallback is a free-text input if we wanted
      }
    })();
  }, [ownerOnly]);

  const rangeLabel = useMemo(() => {
    switch (filterSinceHours) {
      case "1": return "last hour";
      case "24": return "last 24h";
      case "168": return "last 7 days";
      case "720": return "last 30 days";
      default: return "all time";
    }
  }, [filterSinceHours]);

  if (ownerOnly) {
    return (
      <div className="rounded-2xl border border-amber-500/25 bg-amber-500/6 p-8 text-center" data-testid="audit-owner-only">
        <ShieldAlert className="w-8 h-8 text-amber-300 mx-auto mb-3" />
        <h3 className="text-white font-display text-lg">Owner-only view</h3>
        <p className="text-sm text-white/60 mt-2 max-w-md mx-auto leading-relaxed">
          The audit log is restricted to owner-role admins to prevent an insider
          from monitoring who&apos;s watching. Ask an owner to promote your account
          if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-audit-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 text-xs text-white/55">
          <Filter className="w-3.5 h-3.5" />
          Filter
        </div>
        <div className="relative">
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            data-testid="audit-filter-action"
            className="appearance-none bg-[#141414] border border-white/10 rounded-lg text-sm text-white pl-3 pr-8 py-2 focus:border-[#D4AF37]/60 focus:outline-none min-w-[220px]"
          >
            <option value="">All actions</option>
            {availableActions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={filterSinceHours}
            onChange={(e) => setFilterSinceHours(e.target.value)}
            data-testid="audit-filter-range"
            className="appearance-none bg-[#141414] border border-white/10 rounded-lg text-sm text-white pl-3 pr-8 py-2 focus:border-[#D4AF37]/60 focus:outline-none"
          >
            <option value="">All time</option>
            <option value="1">Last hour</option>
            <option value="24">Last 24 h</option>
            <option value="168">Last 7 days</option>
            <option value="720">Last 30 days</option>
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        </div>
        <div className="ml-auto text-xs text-white/40 inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Auto-refresh every 30 s
        </div>
        <button
          onClick={load}
          data-testid="audit-refresh"
          className="text-xs text-white/55 hover:text-white/85 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 transition"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-500/25 bg-rose-500/8 px-4 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      <div className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/50 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-normal">When</th>
              <th className="text-left px-4 py-3 font-normal">Actor</th>
              <th className="text-left px-4 py-3 font-normal">Action</th>
              <th className="text-left px-4 py-3 font-normal">Target</th>
              <th className="text-left px-4 py-3 font-normal">Metadata</th>
              <th className="text-left px-4 py-3 font-normal">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-white/40"><Loader2 className="w-5 h-5 inline animate-spin" /></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-white/40 text-xs">
                  No audit events matching {filterAction || "any action"} · {rangeLabel}.
                </td>
              </tr>
            )}
            {items.map((row) => (
              <tr
                key={row.event_id}
                data-testid={`audit-row-${row.event_id}`}
                className="border-t border-white/6 hover:bg-white/[0.02] align-top"
              >
                <td className="px-4 py-3 text-white/60 text-xs whitespace-nowrap">{new Date(row.at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${ACTOR_STYLE[row.actor_type] || "bg-white/40"}`} />
                    <div>
                      <div className="text-white/80 text-xs">{row.actor_email || <span className="text-white/40">{row.actor_type || "unknown"}</span>}</div>
                      {row.actor_id && <div className="text-white/35 text-[10px] font-mono">{row.actor_id}</div>}
                    </div>
                  </div>
                </td>
                <td className={`px-4 py-3 text-xs font-medium ${actionTint(row.action)}`}>{row.action}</td>
                <td className="px-4 py-3 text-white/60 text-xs">
                  {row.target_type ? (
                    <>
                      <div>{row.target_type}</div>
                      {row.target_id && <div className="text-white/35 font-mono text-[10px]">{row.target_id}</div>}
                    </>
                  ) : <span className="text-white/25">—</span>}
                </td>
                <td className="px-4 py-3 text-white/55 text-xs max-w-xs">
                  {row.metadata && Object.keys(row.metadata).length > 0 ? (
                    <code className="font-mono text-[10px] block whitespace-pre-wrap break-words">
                      {JSON.stringify(row.metadata)}
                    </code>
                  ) : <span className="text-white/25">—</span>}
                </td>
                <td className="px-4 py-3 text-white/50 text-xs font-mono">{row.ip || <span className="text-white/25">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-white/40">
        <span>Showing {items.length} of {total} events</span>
        <div className="flex gap-1">
          <button
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            data-testid="audit-page-prev"
            className="p-1.5 rounded hover:bg-white/8 disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            disabled={skip + PAGE_SIZE >= total}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            data-testid="audit-page-next"
            className="p-1.5 rounded hover:bg-white/8 disabled:opacity-30"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
